"use client";

import { useEffect, useState } from "react";

/**
 * Watch a set of section ids and return whichever one is currently most visible.
 *
 * Uses a single IntersectionObserver so all sections share one observer instance.
 * `rootMargin` is biased toward the upper half of the viewport (after the 72px
 * sticky header is offset) so the nav highlight matches what a reader is
 * actually focused on, not what's about to leave the screen.
 */
export function useActiveSection(
  sectionIds: readonly string[],
  options?: { headerOffset?: number }
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const headerOffset = options?.headerOffset ?? 72;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    const ratios = new Map<string, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let best: { id: string; ratio: number } | null = null;
        for (const [id, ratio] of ratios) {
          if (ratio > 0 && (!best || ratio > best.ratio)) best = { id, ratio };
        }
        setActiveId(best?.id ?? null);
      },
      {
        threshold: [0.1, 0.25, 0.5, 0.75],
        rootMargin: `-${headerOffset}px 0px -50% 0px`,
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sectionIds, headerOffset]);

  return activeId;
}
